import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { cardPull, glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { AvailableCompetitions } from './AvailableCompetitions/index.tsx';
import { Clues } from './Clues/index.tsx';
import { DrawAndOdds } from './DrawAndOdds/index.tsx';
import { HowItWorks } from './HowItWorks/index.tsx';
import type {
  CardPullRulesDialog_cardPullConfig,
  CardPullRulesDialog_cardShardsChest,
} from './__generated__/index.graphql.ts';

const DialogRoot = styled(Vertical).attrs({
  gap: 3,
})`
  width: 100%;
`;
const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

const StyledTab = styled(Tab)`
  @media ${tabletAndAbove} {
    width: calc((100% / 4) - var(--unit));
  }
`;

export enum TabValue {
  HOW_IT_WORKS = 'howItWorks',
  AVAILABLE_COMPETITIONS = 'availableCompetitions',
  DRAW_AND_ODDS = 'drawAndOdds',
  CLUES = 'clues',
}

const tabs = [
  {
    value: TabValue.HOW_IT_WORKS,
    label: <FormattedMessage {...glossary.howItWorks} />,
  },
  {
    value: TabValue.CLUES,
    label: <FormattedMessage {...cardPull.clues} />,
  },
  {
    value: TabValue.AVAILABLE_COMPETITIONS,
    label: <FormattedMessage {...cardPull.availableCompetitions} />,
  },
  {
    value: TabValue.DRAW_AND_ODDS,
    label: <FormattedMessage {...cardPull.drawAndOdds} />,
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
  initialTab?: TabValue;
  cardShardsChest: CardPullRulesDialog_cardShardsChest;
  cardPullConfigs: CardPullRulesDialog_cardPullConfig[];
};

export const CardPullRulesDialog = ({
  open,
  onClose,
  initialTab = TabValue.HOW_IT_WORKS,
  cardShardsChest,
  cardPullConfigs,
}: Props) => {
  const [tab, setTab] = useState(initialTab);
  const { availableCompetitions } = cardShardsChest;
  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      title={
        <BrandM>
          <FormattedMessage {...cardPull.rulesAndEligibility} />
        </BrandM>
      }
    >
      <DialogRoot>
        <TabBar fullWidth value={tab} variant="flat">
          {tabs.map(({ value, label }) => (
            <StyledTab
              key={value}
              value={value}
              onClick={() => setTab(value)}
              preventScrollReset
            >
              {label}
            </StyledTab>
          ))}
        </TabBar>
        <Content>
          {tab === TabValue.HOW_IT_WORKS && <HowItWorks />}
          {tab === TabValue.CLUES && (
            <Clues
              cardPullConfigs={cardPullConfigs}
              rarity={cardShardsChest.rarity}
            />
          )}
          {tab === TabValue.AVAILABLE_COMPETITIONS && (
            <AvailableCompetitions competitions={availableCompetitions} />
          )}
          {tab === TabValue.DRAW_AND_ODDS && (
            <DrawAndOdds cardShardsChest={cardShardsChest} />
          )}
        </Content>
      </DialogRoot>
    </Dialog>
  );
};

CardPullRulesDialog.fragments = {
  cardPullConfig: gql`
    fragment CardPullRulesDialog_cardPullConfig on CardPullConfig {
      ...Clues_cardPullConfig
    }
    ${Clues.fragments.cardPullConfig}
  ` as TypedDocumentNode<CardPullRulesDialog_cardPullConfig>,
  cardShardsChest: gql`
    fragment CardPullRulesDialog_cardShardsChest on CardShardsChest {
      id
      rarity
      availableCompetitions {
        slug
        ...AvailableCompetitions_competition
      }
      ...DrawAndOdds_cardShardsChest
    }
    ${AvailableCompetitions.fragments.competition}
    ${DrawAndOdds.fragments.cardShardsChest}
  ` as TypedDocumentNode<CardPullRulesDialog_cardShardsChest>,
};
